import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { LuSave, LuLoader, LuPlus, LuX } from "react-icons/lu";

const Settings = ({ isSidebarOpen = true }) => {
    const [settings, setSettings] = useState({
        // OPENROUTER_API_KEY: "", // Deprecated
        // OPENROUTER_MODEL_NAME: "", // Deprecated
        OPENALGO_API: "",
        OPENALGO_URL: ""
    });
    const [aiKeys, setAiKeys] = useState([]);
    const [newKey, setNewKey] = useState({ provider: 'OPENROUTER', nickname: '', api_key: '', model: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingKeyId, setEditingKeyId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, keysRes] = await Promise.all([
                api.get("/settings"),
                api.get("/ai-keys")
            ]);

            if (settingsRes.data.success) {
                setSettings(prev => ({ ...prev, ...settingsRes.data.data }));
            }
            if (keysRes.data.success) {
                setAiKeys(keysRes.data.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleNewKeyChange = (e) => {
        const { name, value } = e.target;
        setNewKey(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const response = await api.post("/settings/bulk", settings);
            if (response.data.success) {
                toast.success("Settings saved successfully");
            } else {
                toast.error("Failed to save settings");
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            toast.error("Error saving settings");
        } finally {
            setSaving(false);
        }
    };

    const handleAddKey = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (editingKeyId) {
                response = await api.put(`/ai-keys/${editingKeyId}`, newKey);
            } else {
                response = await api.post("/ai-keys", newKey);
            }

            if (response.data.success) {
                toast.success(editingKeyId ? "Key updated successfully" : "Key added successfully");
                setNewKey({ provider: 'OPENROUTER', nickname: '', api_key: '', model: '' });
                setEditingKeyId(null);
                setShowModal(false);
                fetchData(); // Reload keys
            } else {
                toast.error(response.data.message || "Failed to save key");
            }
        } catch (error) {
            toast.error("Error saving key");
        }
    };

    const handleEditClick = (key) => {
        setNewKey({
            provider: key.provider,
            nickname: key.nickname,
            api_key: '', // Keep empty to not show existing key, or show placeholder? Better empty implies no change if not typed.
            model: key.model
        });
        setEditingKeyId(key.id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingKeyId(null);
        setNewKey({ provider: 'OPENROUTER', nickname: '', api_key: '', model: '' });
    };

    const handleDeleteKey = async (id) => {
        if (!window.confirm("Are you sure you want to delete this key?")) return;
        try {
            const response = await api.delete(`/ai-keys/${id}`);
            if (response.data.success) {
                toast.success("Key deleted");
                fetchData();
            }
        } catch (error) {
            toast.error("Error deleting key");
        }
    };

    const handleSetActive = async (id) => {
        try {
            const response = await api.post("/ai-keys/set-active", { id });
            if (response.data.success) {
                toast.success("Active key updated");
                fetchData();
            }
        } catch (error) {
            toast.error("Error updating active key");
        }
    };

    if (loading) {
        return (
            <div className={`${isSidebarOpen ? "col-lg-9" : "col-lg-12"} pt-4 pb-2 pb-sm-4`}>
                <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${isSidebarOpen ? "col-lg-9" : "col-lg-12"} pt-4 pb-2 pb-sm-4`}>
            <div className="row mb-4">
                <div className="col">
                    <h1 className="h3 mb-0 text-gray-800">System Settings</h1>
                    <p className="text-muted">Manage system-wide configuration keys.</p>
                </div>
            </div>

            {/* AI Configure Section */}
            <div className="card shadow-sm mb-4">
                <div className="card-header py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Manage OpenRouter Keys</h5>
                    <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={() => { handleCloseModal(); setShowModal(true); }}>
                        <LuPlus /> Add Key
                    </button>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Active</th>
                                    <th>Nickname</th>
                                    <th>Model</th>
                                    <th>API Key (Masked)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aiKeys.map(key => (
                                    <tr key={key.id} className={key.is_active ? "table-success" : ""}>
                                        <td className="text-center align-middle">
                                            <input
                                                type="radio"
                                                name="activeKey"
                                                checked={key.is_active === 1}
                                                onChange={() => handleSetActive(key.id)}
                                                style={{ cursor: "pointer", width: "18px", height: "18px" }}
                                            />
                                        </td>
                                        <td className="align-middle">{key.nickname}</td>
                                        <td className="align-middle">{key.model}</td>
                                        <td className="align-middle">{key.api_key}</td>
                                        <td className="align-middle">
                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditClick(key)}>
                                                Edit
                                            </button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteKey(key.id)}>
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {aiKeys.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted py-3">No keys found. Add one above.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* General Settings Section */}
            <div className="card shadow-sm">
                <div className="card-header py-3">
                    <h5 className="mb-0">General Configuration</h5>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSaveSettings} autoComplete="off">

                        <div className="mb-4">
                            <h6 className="card-subtitle mb-3 text-muted">OpenAlgo Configuration</h6>
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <label htmlFor="OPENALGO_API" className="form-label">OpenAlgo API Key</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="OPENALGO_API"
                                        name="OPENALGO_API"
                                        value={settings.OPENALGO_API}
                                        onChange={handleChange}
                                        placeholder="Your OpenAlgo API Key"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label htmlFor="OPENALGO_URL" className="form-label">OpenAlgo URL</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="OPENALGO_URL"
                                        name="OPENALGO_URL"
                                        value={settings.OPENALGO_URL}
                                        onChange={handleChange}
                                        placeholder="http://localhost:5000"
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end">
                            <button
                                type="submit"
                                className="btn btn-primary d-flex align-items-center gap-2"
                                disabled={saving}
                            >
                                {saving ? <LuLoader className="animate-spin" /> : <LuSave />}
                                {saving ? "Saving..." : "Save Settings"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Add Key Modal */}
            {showModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{editingKeyId ? "Edit OpenRouter Key" : "Add OpenRouter Key"}</h5>
                                    <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                                </div>
                                <form onSubmit={handleAddKey} autoComplete="off">
                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label className="form-label">Nickname</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="nickname"
                                                placeholder="e.g. My Claude"
                                                value={newKey.nickname}
                                                onChange={handleNewKeyChange}
                                                required
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Model</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="model"
                                                placeholder="e.g. google/gemini-pro"
                                                value={newKey.model}
                                                onChange={handleNewKeyChange}
                                                required
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">API Key {editingKeyId && <span className="text-muted small">(Leave blank (empty) to keep unchanged)</span>}</label>
                                            <input
                                                type="password"
                                                className="form-control"
                                                name="api_key"
                                                placeholder={editingKeyId ? "Enter new key to update" : "sk-or-..."}
                                                value={newKey.api_key}
                                                onChange={handleNewKeyChange}
                                                required={!editingKeyId}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                                        <button type="submit" className="btn btn-success ms-2">{editingKeyId ? "Update Key" : "Add Key"}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Settings;
